import { defineStore } from 'pinia';
import { onMounted, ref, watch } from 'vue';

// Constants for theme-related values
export const THEME_MODE_KEY = 'themeMode';
export const DARK_MODE_VALUE = 'dark';
export const LIGHT_MODE_VALUE = 'light';
export const DARK_THEME_CLASS = 'app-dark';

// Function to check system preference
export const getSystemPreference = (): boolean => {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
};

// Function to get saved preference from localStorage
export const getSavedPreference = (): boolean | null => {
    const savedThemeMode = localStorage.getItem(THEME_MODE_KEY);
    return savedThemeMode !== null ? savedThemeMode === DARK_MODE_VALUE : null;
};

// Function to apply theme based on preference
export const applyTheme = (isDark: boolean): void => {
    if (isDark) {
        document.documentElement.classList.add(DARK_THEME_CLASS);
    } else {
        document.documentElement.classList.remove(DARK_THEME_CLASS);
    }
};

// Function to initialize theme (can be used before Pinia is available)
export const initializeTheme = (): void => {
    const savedPreference = getSavedPreference();
    const systemPreference = getSystemPreference();
    // §TODO 09.12.2025/DN LIMS-22 DarkMode implementieren
    const isDark = false; //savedPreference !== null ? savedPreference : systemPreference;

    applyTheme(isDark);
};

export const useThemeStore = defineStore('theme', () => {
    // State - initialize with system preference
    // §TODO 09.12.2025/DN LIMS-22 DarkMode implementieren
    const isDarkMode = ref<boolean>(false); // ref<boolean>(getSavedPreference() ?? getSystemPreference());

    // Initialize theme when component using the store is mounted
    onMounted(() => {
        // Apply the theme based on current state
        applyTheme(isDarkMode.value);
    });

    // Watch for changes to persist in localStorage
    watch(isDarkMode, (newValue) => {
        localStorage.setItem(THEME_MODE_KEY, newValue ? DARK_MODE_VALUE : LIGHT_MODE_VALUE);
    });

    // Actions
    const toggleTheme = () => {
        isDarkMode.value = !isDarkMode.value;
        applyTheme(isDarkMode.value);
    };

    const setDarkMode = () => {
        isDarkMode.value = true;
        applyTheme(true);
    };

    const setLightMode = () => {
        isDarkMode.value = false;
        applyTheme(false);
    };

    return {
        // State
        isDarkMode,

        // Actions
        toggleTheme,
        setDarkMode,
        setLightMode
    };
});
